/**
 * Tenant colour application.
 *
 * The stylesheet is built on HSL CSS variables (see `src/index.css`), so a
 * broker's hex `primaryColor` from `/tenant/current` is converted to HSL
 * components and injected on `:root` — every `bg-primary`, badge and button
 * flips at once without touching components.
 *
 * Two rules:
 *   - light and dark carry *separate* action colours. A 25%-lightness brand
 *     colour is right on white and unreadable on #171A1D, so in dark mode
 *     the same hue is served light (and its label goes dark).
 *   - focus stays its own colour. `--ring` is the blue focus token in both
 *     themes and is never brand-tinted, so the 2px ring is always visible.
 */

/** Parse a `#RRGGBB` string to {r,g,b} 0-255. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/**
 * RGB → HSL. Returns `{h: 0–360, s: 0–100, l: 0–100}` integers (rounded)
 * so the CSS variable lands in the same shape as the hand-authored
 * `--primary: 333 86% 56%`.
 */
function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hexToHslString(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return `${h} ${s}% ${l}%`;
}

const TENANT_VARS = ["--primary", "--primary-fg", "--primary-soft", "--accent", "--accent-fg"];

/** Where the boot script in index.html looks for the derived values. */
const cacheKey = (mode: "light" | "dark") => `wachsen:brand:${mode}`;

function cache(mode: "light" | "dark", vars: Record<string, string> | null) {
  try {
    if (vars) window.localStorage.setItem(cacheKey(mode), JSON.stringify(vars));
    else {
      window.localStorage.removeItem(cacheKey("light"));
      window.localStorage.removeItem(cacheKey("dark"));
    }
  } catch {
    // Private mode blocks storage; the only cost is one frame of the default
    // palette on the next load.
  }
}

/**
 * Apply (or clear) the tenant's brand colour on the document root for the
 * appearance currently in use. Clearing falls back to the packaged
 * palette in index.css, which is what this deployment wants anyway.
 */
export function applyTenantTheme(
  hexColor: string | null | undefined,
  mode: "light" | "dark" = "light",
) {
  const root = document.documentElement;
  if (!hexColor) {
    TENANT_VARS.forEach((v) => root.style.removeProperty(v));
    cache(mode, null);
    return;
  }
  const parsed = hexToRgb(hexColor);
  if (!parsed) return;
  const { h, s, l } = rgbToHsl(parsed.r, parsed.g, parsed.b);

  let primary: string;
  let onPrimary: string;
  let accent: string;

  if (mode === "dark") {
    // Same hue, served light enough to read on a near-black surface; the
    // label on it then has to be dark. Very desaturated brands stay muted.
    const dl = Math.max(70, Math.min(82, l + 50));
    const ds = Math.min(s, 70);
    primary = `${h} ${ds}% ${dl}%`;
    onPrimary = "210 11% 18%";                       // graphite
    accent = `${h} ${ds}% ${Math.min(90, dl + 6)}%`; // a touch lighter
  } else {
    primary = `${h} ${s}% ${l}%`;
    // White or graphite label, whichever the fill can carry.
    const luminance = (0.299 * parsed.r + 0.587 * parsed.g + 0.114 * parsed.b) / 255;
    onPrimary = luminance > 0.55 ? "210 11% 18%" : "0 0% 100%";
    accent = `${h} ${Math.min(100, s + 6)}% ${Math.max(0, l - 9)}%`;
  }

  const vars: Record<string, string> = {
    "--primary": primary,
    "--primary-fg": onPrimary,
    "--primary-soft": `${primary} / ${mode === "dark" ? 0.16 : 0.08}`,
    "--accent": accent,
    "--accent-fg": onPrimary,
    // `--ring` is deliberately absent: focus is its own, always-visible colour.
  };
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

  // Cache for the next load, so the boot script can set these before the
  // first paint instead of the action colour changing once /tenant/current
  // answers.
  cache(mode, vars);
}
